/**
 * Checkout Store
 *
 * Strongly-typed Redux store for 3-step checkout flow
 * Following cart store patterns with WooCommerce-style checkout
 *
 * @package Enterprise\BookingSystem
 */

/**
 * WordPress dependencies
 */
import { createReduxStore, register } from "@wordpress/data";
/**
 * Internal dependencies
 */
import { checkoutApiService } from "../../services/api/checkout-api.service";
import { cartApiService } from "../../services/api/cart-api.service";
import { ACTION_TYPES } from "./action-types";
import {
	validateStep1,
	validateStep2,
	validateStep3,
	validateAllSteps,
} from "./validators";
import type {
	BillingAddress,
	CartItem,
	CheckoutFormData,
	CheckoutState,
	CheckoutStep,
	CustomerInformation,
	PaymentInformation,
	ShippingAddress,
} from "./types";
import type { CartFee } from "../../types/cart.types";

/**
 * Storage key for persisting checkout state
 */
const STORAGE_KEY = "ebs_checkout_v2";

/**
 * Base default state
 */
const BASE_DEFAULT_STATE: CheckoutState = {
	currentStep: 1,
	formData: {
		customer: {
			billing: {
				first_name: "",
				last_name: "",
				email: "",
				phone: "",
				address_1: "",
				address_2: "",
				city: "",
				state: "",
				postcode: "",
				country: "US",
			},
		},
		delivery: {
			shipping: {
				first_name: "",
				last_name: "",
				address_1: "",
				address_2: "",
				city: "",
				state: "",
				postcode: "",
				country: "US",
				phone: "",
			},
			ship_to_billing: true,
		},
		payment: {
			payment_method: "card",
		},
		customer_notes: "",
		marketing_opt_in: false,
	},
	cartId: null,
	cartItems: [],
	subtotal: 0,
	shipping_total: 0,
	tax_total: 0,
	total: 0,
	fees: [],
	fees_total: 0,
	formatted_fees_total: "$0.00",
	formatted_shipping: "$0.00",
	formatted_subtotal: "$0.00",
	formatted_tax: "$0.00",
	formatted_total: "$0.00",
	needsPayment: false,
	errors: {},
	isProcessing: false,
	isValidating: false,
	isLoaded: false,
	orderId: null,
	orderNumber: null,
	stripeClientSecret: null,
	stripePaymentIntentId: null,
	stripePublishableKey: null,
	isLoadingPaymentIntent: false,
	event_date: null,
	event_city: null,
	event_state: null,
};

/**
 * Load initial state - DB is source of truth, localStorage is just cache
 * The fetchCart thunk will hydrate from DB on mount
 */
const DEFAULT_STATE = BASE_DEFAULT_STATE;

/**
 * Actions
 */
const actions = {
	// Navigation
	setStep(step: CheckoutStep) {
		return {
			type: ACTION_TYPES.SET_STEP,
			payload: step,
		};
	},

	nextStep() {
		return {
			type: ACTION_TYPES.NEXT_STEP,
		};
	},

	prevStep() {
		return {
			type: ACTION_TYPES.PREV_STEP,
		};
	},

	// Form updates
	updateCustomerInfo(billing: Partial<BillingAddress>) {
		return {
			type: ACTION_TYPES.UPDATE_BILLING_ADDRESS,
			payload: billing,
		};
	},

	updateShippingAddress(shipping: Partial<ShippingAddress>) {
		return {
			type: ACTION_TYPES.UPDATE_SHIPPING_ADDRESS,
			payload: shipping,
		};
	},

	updatePaymentInfo(payment: Partial<PaymentInformation>) {
		return {
			type: ACTION_TYPES.UPDATE_PAYMENT_INFO,
			payload: payment,
		};
	},

	setShipToBilling(shipToBilling: boolean) {
		return {
			type: ACTION_TYPES.SET_SHIP_TO_BILLING,
			payload: shipToBilling,
		};
	},

	// Cart
	setCartId(cartId: number) {
		return {
			type: ACTION_TYPES.SET_CART_ID,
			payload: cartId,
		};
	},

	setCartItems(items: readonly CartItem[]) {
		return {
			type: ACTION_TYPES.SET_CART_ITEMS,
			payload: items,
		};
	},

	setTotals(totals: {
		fees?: CartFee[];
		fees_total?: number;
		formatted_fees_total?: string;
		formatted_shipping: string;
		formatted_subtotal: string;
		formatted_tax: string;
		formatted_total: string;
		needsPayment?: boolean;
		shipping_total: number;
		subtotal: number;
		tax_total: number;
		total: number;
	}) {
		return {
			type: ACTION_TYPES.SET_TOTALS,
			payload: totals,
		};
	},

	// Validation
	setErrors(errors: Record<string, string>) {
		return {
			type: ACTION_TYPES.SET_ERRORS,
			payload: errors,
		};
	},

	clearErrors() {
		return {
			type: ACTION_TYPES.CLEAR_ERRORS,
		};
	},

	setError(field: string, message: string) {
		return {
			type: ACTION_TYPES.SET_ERROR,
			payload: { field, message },
		};
	},

	clearError(field: string) {
		return {
			type: ACTION_TYPES.CLEAR_ERROR,
			payload: field,
		};
	},

	// Processing
	setProcessing(isProcessing: boolean) {
		return {
			type: ACTION_TYPES.SET_PROCESSING,
			payload: isProcessing,
		};
	},

	setValidating(isValidating: boolean) {
		return {
			type: ACTION_TYPES.SET_VALIDATING,
			payload: isValidating,
		};
	},

	setLoaded(isLoaded: boolean) {
		return {
			type: ACTION_TYPES.SET_LOADED,
			payload: isLoaded,
		};
	},

	// Order result
	setOrderResult(orderId: number, orderNumber: string) {
		return {
			type: ACTION_TYPES.SET_ORDER_RESULT,
			payload: { orderId, orderNumber },
		};
	},

	// Hydration
	hydrate(state: Partial<CheckoutState>) {
		return {
			type: ACTION_TYPES.HYDRATE,
			payload: state,
		};
	},

	persist() {
		return {
			type: ACTION_TYPES.PERSIST,
		};
	},

	clearState() {
		return {
			type: ACTION_TYPES.CLEAR_STATE,
		};
	},

	// Stripe actions
	setPaymentIntent(paymentIntent: {
		clientSecret: string;
		paymentIntentId: string;
		publishableKey: string;
	}) {
		return {
			type: ACTION_TYPES.SET_PAYMENT_INTENT,
			payload: paymentIntent,
		};
	},

	setLoadingPaymentIntent(isLoading: boolean) {
		return {
			type: ACTION_TYPES.SET_LOADING_PAYMENT_INTENT,
			payload: isLoading,
		};
	},

	setPaymentIntentId(paymentIntentId: string) {
		return {
			type: ACTION_TYPES.SET_PAYMENT_INTENT,
			payload: { paymentIntentId },
		};
	},

	clearPaymentIntent() {
		return {
			type: ACTION_TYPES.CLEAR_PAYMENT_INTENT,
		};
	},

	setEventDetails(event_date: null | string, city: null | string, state: null | string) {
		return {
			type: ACTION_TYPES.SET_EVENT_DETAILS,
			payload: { event_date, city, state },
		};
	},

	// Thunks (async functions, not generators)
	fetchCart:
		() =>
		async ({ dispatch }: any) => {
			try {
				const cart: any = await cartApiService.getCart();

				// Map cart items to checkout format.
				const cartItems: CartItem[] = (cart.items || []).map(
					(item: any) => ({
						id: item.id,
					product_id: item.product_id,
					name: item.name,
					quantity: item.quantity,
					price: item.price,
					formatted_price: item.formatted_price,
					total: item.total,
					formatted_total: item.formatted_total,
					variant_id: item.variant_id,
					variant_name: item.variant_name,
					event_date: item.event_date,
					duration_hours: item.duration_hours,
					image_url: item.image_url,
					}),
				);

				dispatch(actions.setCartItems(cartItems));
				dispatch(
					actions.setTotals({
						subtotal: cart.subtotal || 0,
						shipping_total: 0,
						tax_total: 0,
						total: cart.total || 0,
						formatted_subtotal: cart.formattedSubtotal || "$0.00",
						formatted_shipping: "$0.00",
						formatted_tax: "$0.00",
						fees: cart.fees || [],
						fees_total: cart.feesTotal || 0,
						formatted_fees_total: cart.formattedFeesTotal || "$0.00",
						formatted_total: cart.formattedTotal || "$0.00",
						needsPayment: cart.needsPayment ?? cart.total > 0,
					}),
				);

				// Hydrate billing address from cart if available.
				if (cart.billing_address) {
					dispatch(actions.updateCustomerInfo(cart.billing_address));
				}

				// Hydrate shipping address from cart if available.
				if (cart.shipping_address) {
					dispatch(
						actions.updateShippingAddress(cart.shipping_address),
					);
				}

				// Hydrate event details (event_date, ZIP stored in city, area label in state).
				if (cart.event_date || cart.city) {
					dispatch(
						actions.setEventDetails(
							cart.event_date ?? null,
							cart.city ?? null,
							cart.state ?? null,
						),
					);
				}

				// Auto-advance past billing step when billing address is already saved.
				// Don't advance to step 3 — Stripe requires a fresh payment intent each session.
				if (cart.billing_address?.first_name) {
					dispatch(actions.setStep(2));
				}

			// Show warnings if product prices/availability changed.
			if (cart.warnings && cart.warnings.length > 0) {
				// You could dispatch an action to show these in the UI if needed
			}

				dispatch(actions.setLoaded(true));
			} catch (error) {
				console.error("Failed to fetch cart:", error);
				dispatch(actions.setCartItems([]));
				dispatch(actions.setLoaded(true));
			}
		},

	validateCurrentStep:
		() =>
		async ({ dispatch, select }: any) => {
			const state: CheckoutState = select.getState();
			const { currentStep, formData } = state;

			let validation;
			if (currentStep === 1) {
				validation = validateStep1(formData.customer);
			} else if (currentStep === 2) {
				validation = validateStep2(formData.delivery);
			} else {
				validation = validateStep3(formData.payment);
			}

			dispatch(actions.setErrors(validation.errors));
			return validation.isValid;
		},

	proceedToNextStep:
		() =>
		async ({ dispatch, select }: any) => {
			const isValid = await dispatch(actions.validateCurrentStep());

			if (isValid) {
				const state: CheckoutState = select.getState();

				// Save data to backend after each step.
				if (state.currentStep === 1) {
					// Save billing address after Step 1.
					await dispatch(actions.saveBillingAddress());
				} else if (state.currentStep === 2) {
					// Save shipping address after Step 2.
					await dispatch(actions.saveShippingAddress());
				}

				dispatch(actions.nextStep());
				dispatch(actions.clearErrors());
			}
		},

	saveBillingAddress:
		() =>
		async ({ dispatch, select }: any) => {
			const state: CheckoutState = select.getState();
			const billing = state.formData.customer.billing;

			try {
				dispatch(actions.setValidating(true));

				// Save billing address to cart.
				await checkoutApiService.updateAddresses(
					billing,
					billing, // Use billing as temp shipping for now.
					false,
				);

				dispatch(actions.setValidating(false));
			} catch {
				dispatch(actions.setValidating(false));
				dispatch(
					actions.setError(
						"general",
						"Failed to save billing address. Please try again.",
					),
				);
			}
		},

	saveShippingAddress:
		() =>
		async ({ dispatch, select }: any) => {
			const state: CheckoutState = select.getState();
			const billing = state.formData.customer.billing;
			const { shipping, ship_to_billing } = state.formData.delivery;

			try {
				dispatch(actions.setValidating(true));

				// Save both billing and shipping addresses.
				await checkoutApiService.updateAddresses(
					billing,
					shipping,
					ship_to_billing,
				);

				dispatch(actions.setValidating(false));
			} catch {
				dispatch(actions.setValidating(false));
				dispatch(
					actions.setError(
						"general",
						"Failed to save delivery address. Please try again.",
					),
				);
			}
		},

	saveAddresses:
		() =>
		async ({ dispatch, select }: any) => {
			const state: CheckoutState = select.getState();
			const billing = state.formData.customer.billing;
			const { shipping, ship_to_billing } = state.formData.delivery;

			try {
				dispatch(actions.setValidating(true));

				await checkoutApiService.updateAddresses(
					billing,
					shipping,
					ship_to_billing,
				);

				dispatch(actions.setValidating(false));
			} catch {
				dispatch(actions.setValidating(false));
				dispatch(
					actions.setError(
						"general",
						"Failed to save addresses. Please try again.",
					),
				);
			}
		},

	submitCheckout:
		() =>
		async ({ dispatch, select }: any) => {
			// Validate all steps
			const state: CheckoutState = select.getState();
			const validation = validateAllSteps(state.formData);

			if (!validation.isValid) {
				dispatch(actions.setErrors(validation.errors));
				dispatch(
					actions.setError(
						"general",
						"Please correct the errors before submitting.",
					),
				);
				return;
			}

			dispatch(actions.setProcessing(true));
			dispatch(actions.clearErrors());

			try {
				const {
					customer,
					delivery,
					payment,
					marketing_opt_in,
					customer_notes,
				} = state.formData;

				const result = await checkoutApiService.processCheckout({
					cart_id: state.cartId ?? undefined,
					// Customer info from billing address
					first_name: customer.billing.first_name,
					last_name: customer.billing.last_name,
					email: customer.billing.email,
					phone: customer.billing.phone,
					// Addresses
					billing: customer.billing,
					shipping: delivery.shipping,
					ship_to_billing: delivery.ship_to_billing,
					// Payment
					payment_method: payment.payment_method,
					paymentIntentId: state.stripePaymentIntentId ?? undefined,
					// Optional
					marketing_opt_in,
					customer_notes,
				});

				// BaseApiService unwraps the response, so result is directly the data object
				if (result && result.order_id) {
					dispatch(
						actions.setOrderResult(
							result.order_id,
							result.order_number || `ORD-${result.order_id}`,
						),
					);
					dispatch(actions.clearState());

					// Redirect to success page
					if (result.redirect_url) {
						globalThis.location.href = result.redirect_url;
					}
				} else {
					dispatch(
						actions.setError(
							"general",
							"Checkout failed. Please try again.",
						),
					);
					dispatch(actions.setProcessing(false));
				}
			} catch {
				dispatch(
					actions.setError(
						"general",
						"An error occurred during checkout. Please try again.",
					),
				);
				dispatch(actions.setProcessing(false));
			}
		},

	createPaymentIntent:
		() =>
		async ({ dispatch }: any) => {
			try {
				dispatch(actions.setLoadingPaymentIntent(true));

				const response = await checkoutApiService.createPaymentIntent();

				dispatch(actions.setPaymentIntent(response));
				dispatch(actions.setLoadingPaymentIntent(false));
			} catch (error) {
				dispatch(actions.setLoadingPaymentIntent(false));
				dispatch(
					actions.setError(
						"payment",
						"Failed to initialize payment. Please try again.",
					),
				);
			}
		},

	/**
	 * Recompute area-derived fees (delivery + tax) from a ZIP check result.
	 * Product-level fees (variant extras) are preserved.
	 * Call whenever the user validates a new ZIP so the order summary updates
	 * immediately without waiting for a round-trip to the server.
	 */
	refreshTotalsFromZip:
		( taxRate: number, deliveryFeeCents: number ) =>
		( { dispatch, select }: any ) => {
			const state: CheckoutState = select.getState();
			const subtotal = state.subtotal;
			const currency = ( globalThis as any ).ebsConfig?.currency ?? 'USD';

			const fmt = ( cents: number ) =>
				new Intl.NumberFormat( 'en-US', { style: 'currency', currency } ).format( cents / 100 );

			// Keep any product-level fees; strip only the area-derived ones.
			const otherFees: CartFee[] = ( state.fees ?? [] ).filter(
				( f: CartFee ) => f.type !== 'delivery' && f.type !== 'tax',
			);

			const areaFees: CartFee[] = [];

			if ( deliveryFeeCents > 0 ) {
				areaFees.push( {
					type:      'delivery',
					name:      'Travel Fee',
					amount:    deliveryFeeCents,
					formatted: fmt( deliveryFeeCents ),
				} );
			}

			if ( taxRate > 0 ) {
				const taxCents = Math.round( subtotal * taxRate / 100 );
				const taxLabel = parseFloat( taxRate.toFixed( 3 ) ).toString();
				areaFees.push( {
					type:      'tax',
					name:      `Tax (${ taxLabel }%)`,
					amount:    taxCents,
					formatted: fmt( taxCents ),
				} );
			}

			const allFees  = [ ...otherFees, ...areaFees ];
			const feesTotal = allFees.reduce( ( sum, f ) => sum + f.amount, 0 );
			const total     = subtotal + feesTotal;

			dispatch( actions.setTotals( {
				subtotal,
				shipping_total:        0,
				tax_total:             0,
				total,
				formatted_subtotal:    fmt( subtotal ),
				formatted_shipping:    '$0.00',
				formatted_tax:         '$0.00',
				fees:                  allFees,
				fees_total:            feesTotal,
				formatted_fees_total:  fmt( feesTotal ),
				formatted_total:       fmt( total ),
				needsPayment:          total > 0,
			} ) );
		},

	/**
	 * Apply a full cart API response to the store totals.
	 * Used after updateEventDetails so we get server-authoritative fees
	 * without a separate fetchCart call.
	 */
	applyCartTotals:
		( cart: any ) =>
		( { dispatch }: any ) => {
			dispatch( actions.setTotals( {
				subtotal:             cart.subtotal        ?? 0,
				shipping_total:       0,
				tax_total:            0,
				total:                cart.total           ?? 0,
				formatted_subtotal:   cart.formattedSubtotal  ?? '$0.00',
				formatted_shipping:   '$0.00',
				formatted_tax:        '$0.00',
				fees:                 cart.fees            ?? [],
				fees_total:           cart.feesTotal       ?? 0,
				formatted_fees_total: cart.formattedFeesTotal ?? '$0.00',
				formatted_total:      cart.formattedTotal  ?? '$0.00',
				needsPayment:         cart.needsPayment    ?? ( cart.total ?? 0 ) > 0,
			} ) );
		},
};

/**
 * Selectors
 */
const selectors = {
	getState(state: CheckoutState): CheckoutState {
		return state;
	},

	getCurrentStep(state: CheckoutState): CheckoutStep {
		return state.currentStep;
	},

	getFormData(state: CheckoutState): CheckoutFormData {
		return state.formData;
	},

	getCustomerInfo(state: CheckoutState): CustomerInformation {
		return state.formData.customer;
	},

	getBillingAddress(state: CheckoutState): BillingAddress {
		return state.formData.customer.billing;
	},

	getShippingAddress(state: CheckoutState): ShippingAddress {
		return state.formData.delivery.shipping;
	},

	isShipToBilling(state: CheckoutState): boolean {
		return state.formData.delivery.ship_to_billing;
	},

	getPaymentInfo(state: CheckoutState): PaymentInformation {
		return state.formData.payment;
	},

	getCartItems(state: CheckoutState): readonly CartItem[] {
		return state.cartItems;
	},

	getTotals(state: CheckoutState) {
		return {
			subtotal: state.subtotal,
			shipping_total: state.shipping_total,
			tax_total: state.tax_total,
			total: state.total,
			formatted_subtotal: state.formatted_subtotal,
			formatted_shipping: state.formatted_shipping,
			formatted_tax: state.formatted_tax,
			formatted_total: state.formatted_total,
			fees: state.fees || [],
			fees_total: state.fees_total || 0,
			formatted_fees_total: state.formatted_fees_total || "$0.00",
			needsPayment: state.needsPayment,
		};
	},

	needsPayment(state: CheckoutState): boolean {
		return state.needsPayment;
	},

	getErrors(state: CheckoutState): Readonly<Record<string, string>> {
		return state.errors;
	},

	hasErrors(state: CheckoutState): boolean {
		return Object.keys(state.errors).length > 0;
	},

	isProcessing(state: CheckoutState): boolean {
		return state.isProcessing;
	},

	isValidating(state: CheckoutState): boolean {
		return state.isValidating;
	},

	isLoaded(state: CheckoutState): boolean {
		return state.isLoaded;
	},

	canProceedToNextStep(state: CheckoutState): boolean {
		const { currentStep, formData } = state;

		if (currentStep === 1) {
			const validation = validateStep1(formData.customer);
			return validation.isValid;
		}

		if (currentStep === 2) {
			const validation = validateStep2(formData.delivery);
			return validation.isValid;
		}

		return true;
	},

	getOrderResult(state: CheckoutState) {
		return {
			orderId: state.orderId,
			orderNumber: state.orderNumber,
		};
	},

	getStripeClientSecret(state: CheckoutState): null | string {
		return state.stripeClientSecret;
	},

	getStripePaymentIntentId(state: CheckoutState): null | string {
		return state.stripePaymentIntentId;
	},

	getStripePublishableKey(state: CheckoutState): null | string {
		return state.stripePublishableKey;
	},

	isLoadingPaymentIntent(state: CheckoutState): boolean {
		return state.isLoadingPaymentIntent;
	},

	getEventDetails(state: CheckoutState): { city: null | string; event_date: null | string; state: null | string } {
		return {
			event_date: state.event_date,
			city: state.event_city,
			state: state.event_state,
		};
	},
};

/**
 * Reducer
 */
type ActionTypes = ReturnType<(typeof actions)[keyof typeof actions]>;
type PlainActionTypes = Extract<ActionTypes, { type: string }>;

const reducer = (
	state: CheckoutState = DEFAULT_STATE,
	action: PlainActionTypes,
): CheckoutState => {
	switch (action.type) {
		case ACTION_TYPES.CLEAR_ERROR: {
			const field = action.payload;
			const { [field]: _, ...remainingErrors } = state.errors;
			return {
				...state,
				errors: remainingErrors,
			};
		}

		case ACTION_TYPES.CLEAR_ERRORS: {
			return {
				...state,
				errors: {},
			};
		}

		case ACTION_TYPES.CLEAR_PAYMENT_INTENT: {
			return {
				...state,
				stripeClientSecret: null,
				stripePaymentIntentId: null,
				isLoadingPaymentIntent: false,
			};
		}

		case ACTION_TYPES.SET_EVENT_DETAILS: {
			const { event_date, city, state: eventState } = action.payload as {
				event_date: null | string;
				city: null | string;
				state: null | string;
			};
			return {
				...state,
				event_date,
				event_city: city,
				event_state: eventState,
			};
		}

		case ACTION_TYPES.CLEAR_STATE: {
			// Clear from localStorage.
			try {
				localStorage.removeItem(STORAGE_KEY);
			} catch (error) {
				console.error("Failed to clear checkout state:", error);
			}
			return BASE_DEFAULT_STATE;
		}

		case ACTION_TYPES.HYDRATE: {
			return {
				...state,
				...(action.payload),
			};
		}

		case ACTION_TYPES.NEXT_STEP: {
			return {
				...state,
				currentStep: Math.min(state.currentStep + 1, 3) as CheckoutStep,
			};
		}

		case ACTION_TYPES.PERSIST: {
			// Persist to localStorage
			try {
				localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
			} catch (error) {
				console.error("Failed to persist checkout state:", error);
			}
			return state;
		}

		case ACTION_TYPES.PREV_STEP: {
			return {
				...state,
				currentStep: Math.max(state.currentStep - 1, 1) as CheckoutStep,
			};
		}

		case ACTION_TYPES.SET_CART_ID: {
			return {
				...state,
				cartId: action.payload,
			};
		}

		case ACTION_TYPES.SET_CART_ITEMS: {
			return {
				...state,
				cartItems: action.payload,
			};
		}

		case ACTION_TYPES.SET_ERROR: {
			const { field, message } = action.payload as {
				field: string;
				message: string;
			};
			return {
				...state,
				errors: {
					...state.errors,
					[field]: message,
				},
			};
		}

		case ACTION_TYPES.SET_ERRORS: {
			return {
				...state,
				errors: action.payload,
			};
		}

		case ACTION_TYPES.SET_LOADED: {
			return {
				...state,
				isLoaded: action.payload,
			};
		}

		case ACTION_TYPES.SET_LOADING_PAYMENT_INTENT: {
			return {
				...state,
				isLoadingPaymentIntent: action.payload,
			};
		}

		case ACTION_TYPES.SET_ORDER_RESULT: {
			const { orderId, orderNumber } = action.payload as {
				orderId: number;
				orderNumber: string;
			};
			return {
				...state,
				orderId,
				orderNumber,
			};
		}

		case ACTION_TYPES.SET_PAYMENT_INTENT: {
			const payload = action.payload as {
				clientSecret?: string;
				paymentIntentId?: string;
				publishableKey?: string;
			};
			return {
				...state,
				stripeClientSecret:
					payload.clientSecret || state.stripeClientSecret,
				stripePaymentIntentId:
					payload.paymentIntentId || state.stripePaymentIntentId,
				stripePublishableKey:
					payload.publishableKey || state.stripePublishableKey,
			};
		}

		case ACTION_TYPES.SET_PROCESSING: {
			return {
				...state,
				isProcessing: action.payload,
			};
		}

		case ACTION_TYPES.SET_SHIP_TO_BILLING: {
			const shipToBilling = action.payload;
			return {
				...state,
				formData: {
					...state.formData,
					delivery: {
						...state.formData.delivery,
						ship_to_billing: shipToBilling,
						// Copy billing to shipping if enabled
						shipping: shipToBilling
							? {
									first_name:
										state.formData.customer.billing
											.first_name,
									last_name:
										state.formData.customer.billing
											.last_name,
									address_1:
										state.formData.customer.billing
											.address_1,
									address_2:
										state.formData.customer.billing
											.address_2,
									city: state.formData.customer.billing.city,
									state: state.formData.customer.billing
										.state,
									postcode:
										state.formData.customer.billing
											.postcode,
									country:
										state.formData.customer.billing.country,
									phone: state.formData.customer.billing
										.phone,
							  }
							: state.formData.delivery.shipping,
					},
				},
			};
		}

		case ACTION_TYPES.SET_STEP: {
			return {
				...state,
				currentStep: action.payload,
			};
		}

		case ACTION_TYPES.SET_TOTALS: {
			const totals = action.payload as {
				formatted_shipping: string;
				formatted_subtotal: string;
				formatted_tax: string;
				formatted_total: string;
				needsPayment?: boolean;
				shipping_total: number;
				subtotal: number;
				tax_total: number;
				total: number;
			};
			return {
				...state,
				...totals,
			};
		}

		case ACTION_TYPES.SET_VALIDATING: {
			return {
				...state,
				isValidating: action.payload,
			};
		}

		case ACTION_TYPES.UPDATE_BILLING_ADDRESS: {
			return {
				...state,
				formData: {
					...state.formData,
					customer: {
						...state.formData.customer,
						billing: {
							...state.formData.customer.billing,
							...(action.payload),
						},
					},
				},
			};
		}

		case ACTION_TYPES.UPDATE_PAYMENT_INFO: {
			return {
				...state,
				formData: {
					...state.formData,
					payment: {
						...state.formData.payment,
						...(action.payload),
					},
				},
			};
		}

		case ACTION_TYPES.UPDATE_SHIPPING_ADDRESS: {
			return {
				...state,
				formData: {
					...state.formData,
					delivery: {
						...state.formData.delivery,
						shipping: {
							...state.formData.delivery.shipping,
							...(action.payload),
						},
					},
				},
			};
		}

		default: {
			return state;
		}
	}
};

/**
 * Store configuration
 */
type ConcreteActions   = typeof actions;
type ConcreteSelectors = typeof selectors;

const storeConfig = { actions, reducer, selectors };

export const store = createReduxStore(
	"ebs/checkout",
	storeConfig as { actions: ConcreteActions; selectors: ConcreteSelectors } & typeof storeConfig,
);

register(store);

/**
 * DISABLED: localStorage auto-persist
 *
 * Database (cart) is now the source of truth for checkout data.
 * Party info, billing, and shipping are saved to cart DB via API:
 * - Step 1 → POST /checkout/party-info
 * - Step 2 → POST /checkout/addresses
 * - On mount → GET /cart hydrates store
 *
 * localStorage is no longer used to prevent data desync issues.
 */
// if (globalThis.window !== undefined) {
// 	(globalThis as any).wp?.data?.subscribe(() => {
// 		const state = (globalThis as any).wp?.data
// 			?.select("ebs/checkout")
// 			?.getState?.();
// 		if (state?.isLoaded) {
// 			try {
// 				localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
// 			} catch (error) {
// 				// Ignore storage errors
// 			}
// 		}
// 	});
// }

export type CheckoutStore = typeof store;
